/**
 * Hook do IPCA (#218 backend; aba Juros & Inflação #220) — realizado SGS 433
 * + projetado (implícita ANBIMA) + Focus cross-check.
 *
 * #228 item 2: aceita `dataRef` (as-of da implícita/cupom, YYYY-MM-DD string
 * SEMPRE — nunca Date) pra comparação histórica, com cache module-level
 * CHAVEADO por dataRef (classe do bug #206). `data === null` sem erro = 404
 * (as-of não materializado) → `notFound`. Polling 10min visibilidade-aware
 * (#75); o dado muda 1×/dia.
 */
import { useEffect, useState } from "react";
import { getIpca } from "../api/client";
import type { IpcaResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<IpcaResponse | null>;
}
const cache = new Map<string, CacheEntry>();

export function invalidateIpcaCache(): void {
  cache.clear();
}

export interface UseIpcaState {
  data: IpcaResponse | null;
  isLoading: boolean;
  error: Error | null;
  /** true = API respondeu 404 (data_ref não materializada). */
  notFound: boolean;
}

export function useIpca(dataRef?: string): UseIpcaState {
  const [state, setState] = useState<UseIpcaState>({
    data: null,
    isLoading: true,
    error: null,
    notFound: false,
  });

  useEffect(() => {
    let cancel = false;
    const key = dataRef ?? "latest";

    function load() {
      const now = Date.now();
      const hit = cache.get(key);
      if (!hit || hit.expiresAt < now) {
        cache.set(key, { expiresAt: now + REFETCH_MS, promise: getIpca(dataRef) });
      }
      cache.get(key)!.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null, notFound: data === null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cache.delete(key);
          setState({ data: null, isLoading: false, error: err, notFound: false });
        });
    }

    setState((s) => ({ ...s, isLoading: true }));
    load();
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [dataRef]);

  return state;
}
